import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import {
  LocalHospital,
  EventAvailable,
  MedicalServices,
  Science,
  Phone,
  Email,
  LocationOn,
  Favorite,
  ChildCare,
  Visibility,
  Hearing,
  Psychology,
  AccessibilityNew,
  PregnantWoman,
  Biotech,
  EmergencyShare,
  VerifiedUser,
  Groups,
  Schedule,
  Star,
  ChevronLeft,
  ChevronRight,
  OpenInNew,
} from '@mui/icons-material';
import { mediaService } from '../services/api';

const MEDIA_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

// ─── Data ───────────────────────────────────────────────────────────────────

const stats = [
  { value: '8+', label: 'Years of Excellence' },
  { value: '20,000+', label: 'Happy Patients' },
  { value: '8+', label: 'Specialist Doctors' },
  { value: '24/7', label: 'Emergency & ICU Care' },
];

const specialities = [
  { icon: <PregnantWoman sx={{ fontSize: 36 }} />, title: 'Obstetrics & Gynaecology', color: '#e91e63' },
  { icon: <Favorite sx={{ fontSize: 36 }} />, title: 'Cardiology', color: '#f44336' },
  { icon: <ChildCare sx={{ fontSize: 36 }} />, title: 'Paediatrics', color: '#ff9800' },
  { icon: <Psychology sx={{ fontSize: 36 }} />, title: 'Neurology', color: '#9c27b0' },
  { icon: <AccessibilityNew sx={{ fontSize: 36 }} />, title: 'Orthopaedics', color: '#2196f3' },
  { icon: <Visibility sx={{ fontSize: 36 }} />, title: 'Ophthalmology', color: '#00bcd4' },
  { icon: <Hearing sx={{ fontSize: 36 }} />, title: 'ENT', color: '#4caf50' },
  { icon: <Biotech sx={{ fontSize: 36 }} />, title: 'Pathology & Lab', color: '#795548' },
  { icon: <MedicalServices sx={{ fontSize: 36 }} />, title: 'General Medicine', color: '#607d8b' },
  { icon: <Science sx={{ fontSize: 36 }} />, title: 'Dermatology', color: '#ff5722' },
];

const services = [
  {
    icon: <LocalHospital sx={{ fontSize: 48 }} />,
    title: 'OPD Services',
    description: 'Walk-in consultations with experienced specialists across all departments, Mon – Sat.',
    color: '#1976d2',
  },
  {
    icon: <EmergencyShare sx={{ fontSize: 48 }} />,
    title: '24 / 7 Emergency',
    description: 'Round-the-clock emergency care with fully equipped trauma and critical care units.',
    color: '#d32f2f',
  },
  {
    icon: <Science sx={{ fontSize: 48 }} />,
    title: 'Diagnostic Lab',
    description: 'NABL-accredited laboratory delivering precise results for 500+ tests within hours.',
    color: '#00897b',
  },
  {
    icon: <EventAvailable sx={{ fontSize: 48 }} />,
    title: 'Easy Appointments',
    description: 'Schedule consultations quickly through our streamlined in-clinic booking system.',
    color: '#7b1fa2',
  },
  {
    icon: <PregnantWoman sx={{ fontSize: 48 }} />,
    title: 'Maternity Care',
    description: 'Comprehensive antenatal, delivery, and postnatal care for mother and baby.',
    color: '#e91e63',
  },
  {
    icon: <VerifiedUser sx={{ fontSize: 48 }} />,
    title: 'Health Packages',
    description: 'Preventive health check-up packages tailored for individuals, couples and corporates.',
    color: '#f57c00',
  },
];

const whyUs = [
  { icon: <Groups sx={{ fontSize: 40, color: '#1976d2' }} />, title: '8+ Specialists', desc: 'Qualified doctors across every major discipline.' },
  { icon: <Biotech sx={{ fontSize: 40, color: '#00897b' }} />, title: 'Modern Equipment', desc: 'Latest diagnostic and surgical technology.' },
  { icon: <Schedule sx={{ fontSize: 40, color: '#7b1fa2' }} />, title: 'Minimal Wait Times', desc: 'Efficient scheduling keeps your time valued.' },
  { icon: <Star sx={{ fontSize: 40, color: '#f57c00' }} />, title: 'Patient-First Care', desc: 'Compassionate, personalised treatment every visit.' },
];

// ─── Component ───────────────────────────────────────────────────────────────

const HomePage = () => {
  const navigate = useNavigate();
  const [mediaItems, setMediaItems] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showDiscretion, setShowDiscretion] = useState(false);
  const [discretionCountdown, setDiscretionCountdown] = useState(5);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      const response = await mediaService.getAllMedia();
      setMediaItems(response.data);
    } catch (error) {
      console.error('Error loading media:', error);
    }
  };

  // When slide changes, check if it needs viewer discretion overlay
  const goToSlide = useCallback((index, items) => {
    const list = items || mediaItems;
    const target = list[index];
    setCurrentSlide(index);
    if (target?.viewerDiscretion) {
      setShowDiscretion(true);
      setDiscretionCountdown(5);
    } else {
      setShowDiscretion(false);
    }
  }, [mediaItems]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % mediaItems.length);
  }, [currentSlide, mediaItems.length, goToSlide]);

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + mediaItems.length) % mediaItems.length);
  };

  // Countdown timer for viewer discretion overlay
  useEffect(() => {
    if (!showDiscretion) return;
    if (discretionCountdown <= 0) {
      setShowDiscretion(false);
      return;
    }
    const t = setTimeout(() => setDiscretionCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showDiscretion, discretionCountdown]);

  // Pause auto-advance while discretion overlay is showing
  useEffect(() => {
    if (mediaItems.length <= 1 || isPaused || showDiscretion) return;
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [mediaItems.length, isPaused, nextSlide, showDiscretion]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ fontFamily: 'Poppins, sans-serif', bgcolor: '#fff' }}>

      {/* ── Top contact bar ── */}
      <Box sx={{ bgcolor: '#0d47a1', color: '#fff', py: 0.6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Phone sx={{ fontSize: 14 }} />
                <Typography variant="caption">+91 92098 98331 / +91 72188 80329</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: 14 }} />
                <Typography variant="caption">sawantgyn@gmail.com</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule sx={{ fontSize: 14 }} />
                <Typography variant="caption">Open 24 Hours &nbsp;|&nbsp; Emergency: 24/7</Typography>
              </Box>
            </Box>
            <Chip label="24/7 Emergency" size="small" sx={{ bgcolor: '#d32f2f', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
          </Box>
        </Container>
      </Box>

      {/* ── Navbar ── */}
      <AppBar position="sticky" elevation={2} sx={{ bgcolor: '#fff', color: '#0d47a1' }}>
        <Toolbar sx={{ py: 0.5 }}>
          <LocalHospital sx={{ fontSize: 36, mr: 1.5, color: '#1976d2' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'Montserrat', color: '#0d47a1', lineHeight: 1.2, fontSize: { xs: '0.95rem', md: '1.15rem' } }}>
              Dr. Dhawal Sawant
            </Typography>
            <Typography variant="caption" sx={{ color: '#1976d2', letterSpacing: 1, fontSize: '0.65rem', display: 'block' }}>
              Safe motherhood and expert care for women's on every stage
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{
              bgcolor: '#1976d2',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              '&:hover': { bgcolor: '#0d47a1' },
            }}
          >
            Staff Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* ── Hero ── */}
      <Box id="home" sx={{
        background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)',
        color: '#fff',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* decorative circles */}
        <Box sx={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', top: -100, right: -100 }} />
        <Box sx={{ position: 'absolute', width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', bottom: -50, left: -50 }} />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip label="Dr. Dhawal Sawant" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, mb: 2, letterSpacing: 0.5 }} />
              <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontFamily: 'Montserrat', lineHeight: 1.2, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                Your Health,<br />Our Commitment
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.88, mb: 1.5, fontWeight: 400, lineHeight: 1.7 }}>
                Compassionate, evidence-based care delivered by a team of 8+ specialists — all under one roof.
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.75, mb: 4 }}>
                Serving the community with excellence since 2018.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ bgcolor: '#fff', color: '#0d47a1', fontWeight: 700, px: 4, py: 1.5, borderRadius: 2, '&:hover': { bgcolor: '#e3f2fd' } }}
                >
                  Staff Portal
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="tel:+919209898331"
                  sx={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff', fontWeight: 600, px: 4, py: 1.5, borderRadius: 2, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
                >
                  Call Us
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Stats cards */}
              <Grid container spacing={2}>
                {stats.map((stat, i) => (
                  <Grid item xs={6} key={i}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 3,
                      p: 3,
                      textAlign: 'center',
                    }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '2rem', fontFamily: 'Montserrat', lineHeight: 1 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85, mt: 0.5, display: 'block', letterSpacing: 0.3 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Specialities ── */}
      <Box id="specialities" sx={{ bgcolor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: '#1976d2', fontWeight: 700, letterSpacing: 2 }}>
              What We Treat
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
              Our Specialities
            </Typography>
            <Divider sx={{ width: 60, height: 3, bgcolor: '#1976d2', mx: 'auto', mt: 2, border: 'none' }} />
          </Box>
          <Grid container spacing={2.5}>
            {specialities.map((sp, i) => (
              <Grid item xs={6} sm={4} md={2.4} key={i}>
                <Card elevation={0} sx={{
                  textAlign: 'center',
                  p: 2.5,
                  border: '1px solid #e8eef4',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  '&:hover': { boxShadow: '0 8px 24px rgba(25,118,210,0.12)', borderColor: sp.color, transform: 'translateY(-4px)' },
                }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '50%',
                    bgcolor: `${sp.color}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 1.5, color: sp.color,
                  }}>
                    {sp.icon}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3 }}>
                    {sp.title}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Services ── */}
      <Box id="services" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: '#1976d2', fontWeight: 700, letterSpacing: 2 }}>
              What We Offer
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
              Our Services
            </Typography>
            <Divider sx={{ width: 60, height: 3, bgcolor: '#1976d2', mx: 'auto', mt: 2, border: 'none' }} />
          </Box>
          <Grid container spacing={3}>
            {services.map((service, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card elevation={0} sx={{
                  p: 3, height: '100%',
                  border: '1px solid #e8eef4',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': { boxShadow: `0 12px 32px ${service.color}22`, borderColor: service.color, transform: 'translateY(-4px)' },
                }}>
                  <Box sx={{
                    width: 72, height: 72, borderRadius: 2.5,
                    bgcolor: `${service.color}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mb: 2.5, color: service.color,
                  }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {service.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Why Choose Us ── */}
      <Box sx={{ bgcolor: '#0d47a1', color: '#fff', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: '#90caf9', fontWeight: 700, letterSpacing: 2 }}>
              Why Patients Trust Us
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
              Why Choose Us?
            </Typography>
            <Divider sx={{ width: 60, height: 3, bgcolor: '#90caf9', mx: 'auto', mt: 2, border: 'none' }} />
          </Box>
          <Grid container spacing={4}>
            {whyUs.map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 80, height: 80, borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 2,
                  }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 40, color: '#90caf9' } })}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{item.title}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.7 }}>{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Media Carousel ── */}
      {mediaItems.length > 0 && (
        <Box id="gallery" sx={{ bgcolor: '#f8fafc', py: 8 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="overline" sx={{ color: '#1976d2', fontWeight: 700, letterSpacing: 2 }}>
                Gallery
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
                Healthcare at your Doorstep
              </Typography>
              <Divider sx={{ width: 60, height: 3, bgcolor: '#1976d2', mx: 'auto', mt: 2, border: 'none' }} />
            </Box>

            {/* Carousel */}
            <Box
              sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.15)' }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Slides */}
              <Box sx={{ position: 'relative', width: '100%', height: { xs: 260, sm: 400, md: 520 }, overflow: 'hidden', bgcolor: '#f8fafc' }}>
                {mediaItems.map((item, idx) => (
                  <Box
                    key={item.id}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      transition: 'opacity 0.7s ease, transform 0.7s ease',
                      opacity: idx === currentSlide ? 1 : 0,
                      transform: idx === currentSlide ? 'translateX(0)' : idx < currentSlide ? 'translateX(-60px)' : 'translateX(60px)',
                      pointerEvents: idx === currentSlide ? 'auto' : 'none',
                    }}
                  >
                    <Box
                      component="img"
                      src={item.url?.startsWith('http') ? item.url : `${MEDIA_BASE_URL}${item.url}`}
                      alt={item.title || item.fileName}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                        bgcolor: '#f8fafc',
                      }}
                    />
                    {/* Caption overlay */}
                    {(item.title || item.description || item.hyperlink) && (
                      <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                        px: 4, py: 3, color: '#fff',
                        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2,
                      }}>
                        <Box>
                          {item.title && (
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                              {item.title}
                            </Typography>
                          )}
                          {item.description && (
                            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                              {item.description}
                            </Typography>
                          )}
                        </Box>
                        {item.hyperlink && (
                          <Box
                            component="a"
                            href={item.hyperlink}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View more"
                            sx={{
                              flexShrink: 0,
                              width: 44, height: 44,
                              borderRadius: '50%',
                              bgcolor: 'rgba(255,255,255,0.2)',
                              backdropFilter: 'blur(6px)',
                              border: '1.5px solid rgba(255,255,255,0.5)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff',
                              textDecoration: 'none',
                              transition: 'all 0.2s',
                              '&:hover': { bgcolor: '#1976d2', borderColor: '#1976d2', transform: 'scale(1.1)' },
                            }}
                          >
                            <OpenInNew sx={{ fontSize: 20 }} />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Viewer Discretion Overlay */}
              {showDiscretion && (
                <Box sx={{
                  position: 'absolute', inset: 0, zIndex: 20,
                  bgcolor: 'rgba(0,0,0,0.85)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 2,
                }}>
                  <Box sx={{ fontSize: 56 }}>⚠️</Box>
                  <Typography variant="h4" sx={{ color: '#ffcc00', fontWeight: 800, textAlign: 'center', px: 3 }}>
                    Viewer Discretion Advised
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', px: 4 }}>
                    This image may contain sensitive content.
                  </Typography>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '50%',
                    border: '4px solid #ffcc00',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mt: 1,
                  }}>
                    <Typography variant="h4" sx={{ color: '#ffcc00', fontWeight: 800 }}>
                      {discretionCountdown}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Image will display automatically
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={nextSlide}
                    sx={{ mt: 1, color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}
                  >
                    Skip — Next Image
                  </Button>
                </Box>
              )}

              {/* Prev / Next arrows */}
              {mediaItems.length > 1 && (
                <>
                  <Box
                    onClick={prevSlide}
                    sx={{
                      position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.85)', borderRadius: '50%',
                      width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': { bgcolor: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    <ChevronLeft sx={{ color: '#0d47a1', fontSize: 28 }} />
                  </Box>
                  <Box
                    onClick={nextSlide}
                    sx={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.85)', borderRadius: '50%',
                      width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': { bgcolor: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    <ChevronRight sx={{ color: '#0d47a1', fontSize: 28 }} />
                  </Box>
                </>
              )}

              {/* Dot indicators */}
              {mediaItems.length > 1 && (
                <Box sx={{
                  position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                  display: 'flex', gap: 1, zIndex: 10,
                }}>
                  {mediaItems.map((_, idx) => (
                    <Box
                      key={idx}
                      onClick={() => goToSlide(idx)}
                      sx={{
                        width: idx === currentSlide ? 24 : 8,
                        height: 8, borderRadius: 4,
                        bgcolor: idx === currentSlide ? '#fff' : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Thumbnail strip */}
            {mediaItems.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 2, overflowX: 'auto', pb: 1, justifyContent: mediaItems.length <= 6 ? 'center' : 'flex-start' }}>
                {mediaItems.map((item, idx) => (
                  <Box
                    key={item.id}
                    onClick={() => goToSlide(idx)}
                    sx={{
                      flexShrink: 0,
                      width: 80, height: 56, borderRadius: 2,
                      overflow: 'hidden', cursor: 'pointer',
                      border: idx === currentSlide ? '3px solid #1976d2' : '3px solid transparent',
                      opacity: idx === currentSlide ? 1 : 0.6,
                      transition: 'all 0.2s',
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Box
                      component="img"
                      src={item.url?.startsWith('http') ? item.url : `${MEDIA_BASE_URL}${item.url}`}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', bgcolor: '#f8fafc' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* ── Contact ── */}
      <Box id="contact" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: '#1976d2', fontWeight: 700, letterSpacing: 2 }}>
              Reach Us
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
              Contact Us
            </Typography>
            <Divider sx={{ width: 60, height: 3, bgcolor: '#1976d2', mx: 'auto', mt: 2, border: 'none' }} />
          </Box>
          <Grid container spacing={3} justifyContent="center">
            {[
              { icon: <Phone sx={{ fontSize: 36, color: '#1976d2' }} />, title: 'Phone', lines: ['+91 92098 98331', '+91 72188 80329'], color: '#1976d2' },
              { icon: <Email sx={{ fontSize: 36, color: '#00897b' }} />, title: 'Email', lines: ['sawantgyn@gmail.com'], color: '#00897b' },
              { icon: <LocationOn sx={{ fontSize: 36, color: '#7b1fa2' }} />, title: 'Location', lines: ['Near Hotel Grand Sita, Beside Harinkhede Petrol Pump,', 'Tiroda Road, Gondia (M.H)'], color: '#7b1fa2' },
              { icon: <Schedule sx={{ fontSize: 36, color: '#f57c00' }} />, title: 'Working Hours', lines: ['Open 24 Hours', 'Emergency: 24 / 7'], color: '#f57c00' },
            ].map((c, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card elevation={0} sx={{ textAlign: 'center', p: 3, border: '1px solid #e8eef4', borderRadius: 3, height: '100%', transition: 'all 0.3s', '&:hover': { boxShadow: `0 8px 24px ${c.color}22`, borderColor: c.color } }}>
                  <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: `${c.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    {c.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{c.title}</Typography>
                  {c.lines.map((l, j) => (
                    <Typography key={j} variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>{l}</Typography>
                  ))}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ bgcolor: '#0a1929', color: '#fff', pt: 6, pb: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Brand */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LocalHospital sx={{ fontSize: 28, color: '#90caf9' }} />
                <Typography sx={{ fontWeight: 800, fontFamily: 'Montserrat', fontSize: '1rem', color: '#fff' }}>
                  Dr. Dhawal Sawant
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#90a4ae', lineHeight: 1.8, mb: 2 }}>
                Delivering compassionate, world-class healthcare to our community since 2010. Your health and well-being remain our highest priority.
              </Typography>
              <Chip label="High risk obstetrics and Gynaecology clinic" size="small" sx={{ bgcolor: '#1565c0', color: '#90caf9', fontWeight: 700, letterSpacing: 1, fontSize: '0.65rem' }} />
            </Grid>

            {/* Quick Links */}
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#90caf9', textTransform: 'uppercase', letterSpacing: 1 }}>
                Quick Links
              </Typography>
              {[
                { label: 'Home', id: 'home' },
                { label: 'Services', id: 'services' },
                { label: 'Specialities', id: 'specialities' },
                { label: 'Gallery', id: 'gallery' },
                { label: 'Contact', id: 'contact' },
              ].map((link) => (
                <Typography
                  key={link.id}
                  variant="body2"
                  onClick={() => scrollToSection(link.id)}
                  sx={{ color: '#90a4ae', mb: 0.8, cursor: 'pointer', '&:hover': { color: '#fff' }, transition: 'color 0.2s' }}
                >
                  {link.label}
                </Typography>
              ))}
            </Grid>

            {/* Specialities */}
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#90caf9', textTransform: 'uppercase', letterSpacing: 1 }}>
                Specialities
              </Typography>
              {['Obstetrics & Gynaecology', 'Cardiology', 'Paediatrics', 'Neurology', 'Orthopaedics'].map((s) => (
                <Typography key={s} variant="body2" sx={{ color: '#90a4ae', mb: 0.8 }}>
                  {s}
                </Typography>
              ))}
            </Grid>

            {/* Contact */}
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#90caf9', textTransform: 'uppercase', letterSpacing: 1 }}>
                Contact
              </Typography>
              {[
                { icon: <Phone sx={{ fontSize: 14 }} />, text: '+91 92098 98331 / +91 72188 80329' },
                { icon: <Email sx={{ fontSize: 14 }} />, text: 'sawantgyn@gmail.com' },
                { icon: <LocationOn sx={{ fontSize: 14 }} />, text: 'Near Hotel Grand Sita, Beside Harinkhede Petrol Pump, Tiroda Road, Gondia (M.H)' },
              ].map((c, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Box sx={{ color: '#90caf9', mt: 0.2, flexShrink: 0 }}>{c.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#90a4ae', lineHeight: 1.6 }}>{c.text}</Typography>
                </Box>
              ))}
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#546e7a' }}>
              © 2026 Dr. Dhawal Sawant. All rights reserved.
            </Typography>
            <Typography variant="caption" sx={{ color: '#546e7a' }}>
              Privacy Policy &nbsp;·&nbsp; Terms of Service
            </Typography>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 1.5 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#90caf9' }}>
              Designed &amp; maintained by{' '}
              <Box
                component="a"
                href="https://q-erasoftware.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#90caf9', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                Q-Era Software Pvt. Ltd.
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
